import React, { useEffect, useState, useRef } from "react";
import { api } from "../../lib/api";
import { Fish, Play, Square, Loader2, RotateCw } from "lucide-react";
import { toast } from "sonner";

/**
 * iter121 — Bulk pre-warm fisheries.
 *
 * Walks every UK campsite once and pings OSM Overpass for each, populating
 * `has_nearby_fishery` on the campsite record. Once finished, the "Near a
 * fishery" Browse filter chip is instant for every visitor — no more
 * waiting for organic per-site visits to backfill the data.
 *
 * Throttled to one Overpass call per second (polite to OSM volunteers).
 * Resumes safely: anything cached within the last 30 days is skipped.
 */
function _formatErr(e) {
  return e?.response?.data?.detail || e?.message || "Request failed";
}

export default function FisheriesPrewarmCard() {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  const refresh = async () => {
    try {
      const { data } = await api.get("/admin/maintenance/prewarm-fisheries/status");
      setState(data);
      return data;
    } catch (e) {
      // 404 means the endpoint isn't deployed yet on this env
      if (e?.response?.status !== 404) toast.error(_formatErr(e));
      return null;
    }
  };

  useEffect(() => {
    refresh();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  useEffect(() => {
    if (state?.running) {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(refresh, 5000);
    } else if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, [state?.running]);

  const start = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/maintenance/prewarm-fisheries");
      if (data.started) toast.success("Fisheries pre-warm started");
      else toast.info(`Already running (${data.done}/${data.total})`);
      await refresh();
    } catch (e) {
      toast.error(_formatErr(e));
    } finally { setBusy(false); }
  };

  const stop = async () => {
    setBusy(true);
    try {
      await api.post("/admin/maintenance/prewarm-fisheries/stop");
      toast.info("Stopping after current site finishes…");
      await refresh();
    } catch (e) {
      toast.error(_formatErr(e));
    } finally { setBusy(false); }
  };

  const done = state?.done ?? 0;
  const total = state?.total ?? 0;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const finishedRecently = state?.finished_at && !state?.running;

  return (
    <section className="border hairline bg-white p-5" data-testid="fisheries-prewarm-card">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Fish className="w-5 h-5 text-[#0891b2]" />
          <h2 className="font-display text-lg">Fisheries · bulk pre-warm</h2>
        </div>
        <button
          onClick={refresh}
          className="text-xs mono uppercase tracking-wider text-[#888] hover:text-[#0e0e0e] inline-flex items-center gap-1"
          data-testid="fisheries-prewarm-refresh"
        >
          <RotateCw className="w-3 h-3" /> Refresh
        </button>
      </div>
      <p className="text-xs text-[#555] mb-4 leading-relaxed">
        Walks every UK campsite once and asks OpenStreetMap for fisheries
        within 10 km. Populates the <code>has_nearby_fishery</code> flag so
        the "🎣 Near a fishery" browse filter is instant. ~1 call / second
        (polite to OSM). Skips anything cached &lt; 30 days. Safe to stop and
        resume.
      </p>

      {state?.running && (
        <div className="bg-[#f0f9ff] border border-[#bae6fd] p-3 mb-3" data-testid="prewarm-progress">
          <div className="flex items-center justify-between text-xs mono uppercase tracking-wider mb-2">
            <span className="text-[#0c4a6e]">
              <Loader2 className="inline w-3 h-3 animate-spin mr-1" />
              Running · {done.toLocaleString()} / {total.toLocaleString()} · {pct}%
            </span>
            <span className="text-[#0c4a6e]">
              🎣 {state.with_fisheries ?? 0} with fisheries
            </span>
          </div>
          <div className="w-full bg-white h-2 overflow-hidden border border-[#bae6fd]">
            <div className="h-2 bg-[#0891b2] transition-all" style={{ width: `${pct}%` }} />
          </div>
          {state.current && (
            <div className="text-[11px] text-[#0c4a6e] mt-2 truncate">
              Currently: {state.current}
            </div>
          )}
          {state.errors > 0 && (
            <div className="text-[11px] text-[#7a2400] mt-1">
              ⚠ {state.errors} site(s) failed (logged, will retry on next run)
            </div>
          )}
        </div>
      )}

      {finishedRecently && (
        <div className="bg-[#f0fdf4] border border-[#86efac] p-3 mb-3" data-testid="prewarm-finished">
          <div className="text-xs mono uppercase tracking-wider text-[#14532d]">
            ✓ Last run finished {new Date(state.finished_at).toLocaleString("en-GB")}
          </div>
          <div className="text-[11px] text-[#14532d] mt-1">
            {done.toLocaleString()} of {total.toLocaleString()} sites scanned ·
            <strong className="ml-1">🎣 {state.with_fisheries} with nearby fisheries</strong>
            {state.errors > 0 && ` · ${state.errors} error(s)`}
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        {!state?.running ? (
          <button
            onClick={start}
            disabled={busy}
            className="inline-flex items-center gap-1.5 bg-[#0891b2] text-white px-3 py-1.5 text-xs font-bold uppercase tracking-wider hover:bg-[#0c4a6e] disabled:opacity-50"
            data-testid="prewarm-start-btn"
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
            {state?.finished_at ? "Run again" : "Start pre-warm"}
          </button>
        ) : (
          <button
            onClick={stop}
            disabled={busy}
            className="inline-flex items-center gap-1.5 border-2 border-[#b00020] text-[#b00020] px-3 py-1.5 text-xs font-bold uppercase tracking-wider hover:bg-[#fff0f0] disabled:opacity-50"
            data-testid="prewarm-stop-btn"
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Square className="w-3 h-3" />}
            Stop
          </button>
        )}
        <span className="text-[11px] mono text-[#666] uppercase tracking-wider">
          ~6 hours for 22k sites · safe to leave overnight
        </span>
      </div>
    </section>
  );
}
